'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { useConfirmTrackedLinksMutation } from '@/lib/store/api/trackedLinksApi'

export const TRACKED_LINK_CONFIRMATION_TRANSIENT_RETRY_LIMIT = 2
export const TRACKED_LINK_CONFIRMATION_TRANSIENT_RETRY_DELAY_MS = 1000

function isTransientTrackedLinkConfirmationFailure(error: unknown): boolean {
    if (!error || typeof error !== 'object') return true
    const record = error as { status?: unknown; originalStatus?: unknown }
    if (record.status === 'FETCH_ERROR' || record.status === 'TIMEOUT_ERROR') return true
    if (typeof record.status === 'number' && record.status >= 500) return true
    return (
        record.status === 'PARSING_ERROR' &&
        typeof record.originalStatus === 'number' &&
        record.originalStatus >= 500
    )
}

export function TrackedLinkConfirmation() {
    const [confirmTrackedLinks] = useConfirmTrackedLinksMutation()
    const pathname = usePathname()
    const inFlightRef = useRef(false)
    const succeededRef = useRef(false)

    useEffect(() => {
        let cancelled = false
        let retryTimeoutId: ReturnType<typeof setTimeout> | undefined
        let resolveDelay: (() => void) | undefined

        const delay = (milliseconds: number) =>
            new Promise<void>((resolve) => {
                resolveDelay = resolve
                retryTimeoutId = setTimeout(() => {
                    retryTimeoutId = undefined
                    resolveDelay = undefined
                    resolve()
                }, milliseconds)
            })

        const canConfirmNow = () =>
            !cancelled &&
            isAuthenticatedViaMiddlewareCookie() &&
            document.visibilityState === 'visible'

        const attemptConfirmation = () => {
            if (succeededRef.current || inFlightRef.current) return
            if (!canConfirmNow()) return

            inFlightRef.current = true
            void (async () => {
                try {
                    for (let attempt = 0; ; attempt += 1) {
                        if (!canConfirmNow()) return
                        try {
                            await confirmTrackedLinks().unwrap()
                            succeededRef.current = true
                            return
                        } catch (error) {
                            if (cancelled) return
                            const retryTransient =
                                isTransientTrackedLinkConfirmationFailure(error) &&
                                attempt < TRACKED_LINK_CONFIRMATION_TRANSIENT_RETRY_LIMIT
                            if (!retryTransient) return
                            await delay(TRACKED_LINK_CONFIRMATION_TRANSIENT_RETRY_DELAY_MS)
                        }
                    }
                } finally {
                    if (!cancelled) {
                        inFlightRef.current = false
                    }
                }
            })()
        }

        attemptConfirmation()
        document.addEventListener('visibilitychange', attemptConfirmation)
        window.addEventListener('online', attemptConfirmation)
        return () => {
            cancelled = true
            inFlightRef.current = false
            if (retryTimeoutId !== undefined) clearTimeout(retryTimeoutId)
            resolveDelay?.()
            document.removeEventListener('visibilitychange', attemptConfirmation)
            window.removeEventListener('online', attemptConfirmation)
        }
    }, [confirmTrackedLinks, pathname])

    return null
}
